import { AGENT_HOST_ROLE_SLUG, MACHINE_ENROLLER_ROLE_SLUG } from "./compute-unit-context.js";
import { OrgPermission } from "./org-permissions.js";
import { RESOURCE_GRANT_RESERVED_ORG_PERMISSION_KEYS } from "./permission-key.js";
import "./permissions.js";
import { z } from "zod";
//#region src/federated-token.ts
/**
* The machine-login wire contract of an `external` principal (feature
* 2026-10-01-dp-oidc-federation, D-004, D-007, D-008, D-012).
*
* Two public routes on a data-plane org host, and the same exchange route on the
* control plane:
*
* 1. `POST /v1/auth/token/federated`, the RFC 8693 token exchange. A machine
*    posts an OIDC assertion: one its own IdP minted (GitHub Actions, GCP, a
*    Kubernetes cluster), or one it signed with a private key whose public half an
*    org admin registered. The plane matches the assertion against one trust
*    policy, verifies it, and answers with a 900 s bearer and no refresh token
*    (D-005). core-api (WS-4) and cp-auth-api (WS-8) bind
*    {@link federatedTokenRequestSchema}; the Node, Python and Rust clients (WS-6,
*    WS-9, WS-10) parse {@link federatedTokenResponseSchema}.
* 2. `POST /v1/auth/machine-principals/enroll`, the one-use enroll. A machine
*    presents the enroll token an org admin minted for it and its public JWK; the
*    data plane stores the key and answers with the key id and the issuer the
*    machine's assertions name (D-012). core-api (WS-5) binds
*    {@link machineEnrollRequestSchema}; the clients parse
*    {@link machineEnrollResponseSchema}.
*
* Every schema here is `.strict()` and carries no `.transform()`, so the
* global `ZionStrictValidationPipe` refuses an unknown key with a 400 and
* `createOpenApiDocument()` can represent every schema. A schema refuses on its
* own; it never returns a corrected value. Zion stores no secret for either
* route (D-004): the enroll token is stored as a hash by its writer, and a
* registered key is a public key.
*/
/** The data-plane and control-plane path of the RFC 8693 exchange (D-008). */
const FEDERATED_TOKEN_PATH = "/v1/auth/token/federated";
/** The data-plane path of the one-use enroll an `external` machine calls with its enroll token (D-012). */
const MACHINE_ENROLL_PATH = "/v1/auth/machine-principals/enroll";
/** RFC 8693 §2.1 `grant_type`. The only value the exchange accepts. */
const TOKEN_EXCHANGE_GRANT_TYPE = "urn:ietf:params:oauth:grant-type:token-exchange";
/**
* RFC 8693 §3 `subject_token_type`, and the `issued_token_type` of every
* success response. The only value the exchange accepts.
*/
const SUBJECT_TOKEN_TYPE_JWT = "urn:ietf:params:oauth:token-type:jwt";
/**
* The upper bound on a presented assertion, in characters. A GitHub Actions
* assertion is about 1.2 kB. The route decodes, parses and verifies every byte
* before it authenticates anything, so the bound caps the work one anonymous
* request can ask for. The value is the control plane's, carried over unchanged
* so one client contract serves both planes (D-008).
*/
const MAX_SUBJECT_TOKEN_LENGTH = 8192;
/**
* The longest lifetime an assertion signed with a registered key may claim,
* `exp - iat`, in seconds (D-010 as amended by R-002). `AssertionVerifier` in
* `@zion/platform-identity` refuses a longer one when the issuer resolved to a
* registered key. The SDK signers (WS-6, WS-9, WS-10) mint `exp = iat + 300`.
*/
const FEDERATED_ASSERTION_MAX_LIFETIME_SECONDS = 300;
/**
* The longest lifetime an assertion from a discovered OIDC issuer may claim,
* `exp - iat`, in seconds (R-002). `AssertionVerifier` refuses a longer one when
* the issuer resolved through discovery. A Kubernetes projected service-account
* token lives at least 600 s and a GCP identity token 3,600 s, so a cap of 300 s
* would refuse both issuers the design names.
*/
const OIDC_ASSERTION_MAX_LIFETIME_SECONDS = 3600;
/**
* The longest lifetime of a token the exchange mints, in seconds (D-005). The
* response schema refuses an `expires_in` above it, so a client never trusts a
* longer-lived machine token than the design allows.
*
* It bounds every token that a release-source scoped trust policy did not
* admit. A scoped token has its own bound,
* {@link FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS} (feature
* 2026-10-15-signed-builds, D7: "The 900 s cap of unscoped tokens stays").
*
* One exception: an unscoped trust policy with a `tokenTtlSeconds` value mints
* tokens of that lifetime, up to {@link FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS}
* (plan 2026-10-31-door-long-push, D1). The exchange checks such an answer
* against {@link federatedReleaseTokenResponseSchema} and the policy value.
*/
const FEDERATED_TOKEN_MAX_TTL_SECONDS = 900;
/**
* The longest lifetime of a release-source scoped CP token, in seconds (feature
* 2026-10-15-signed-builds, D7). cp-auth-api reads `CP_FEDERATED_RELEASE_TOKEN_TTL`
* as a whole number from 1 to this value, with this value as the default, and a
* value outside that range stops the boot. The minted lifetime never passes the
* assertion's {@link RUN_DEADLINE_CLAIM} when the assertion carries one.
* {@link federatedReleaseTokenResponseSchema} refuses an `expires_in` above it.
*/
const FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS = 3600;
/**
* The claim of a release-source scoped CP token that names the one app the token
* may publish (feature 2026-10-15-signed-builds, D7, D8, DEF-6). Its value is the
* app uuid in lower case ({@link zionAppClaimSchema}). cp-auth-api's minter
* writes it only when a scoped trust policy admitted the assertion and the CP
* matched exactly one app by its release source. `/v1/auth/me/grants` answers a
* token that carries it with `cp:app:publish` and `cp:app:read` on that app
* alone, and ignores the principal's `resource_grants`.
*/
const ZION_APP_CLAIM = "zion_app";
/**
* The claim of a zion-build job ID token that holds the epoch second at which
* the run's deadline ends (DEF-6). A whole number of seconds
* ({@link runDeadlineClaimSchema}). When the assertion carries it, the scoped
* exchange mints a token that expires no later than that second; an assertion
* whose deadline is at or before the current second gets no token. An assertion
* without it is bounded by {@link FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS} alone.
*/
const RUN_DEADLINE_CLAIM = "run_deadline";
/**
* The claim of a zion-build job ID token that names who started the run:
* `human`, `agent` or `system` (zion-build `runs.submitter_kind`, DEF-6). A
* release-source scoped trust policy requires it as
* `{"submitter_kind":"human"}` through its required claims (D9), so the
* existing exact-match rule applies and no new comparison exists.
*/
const SUBMITTER_KIND_CLAIM = "submitter_kind";
/**
* The values of a {@link SUBMITTER_KIND_CLAIM} claim, in the order of ruling
* R-002 of feature 2026-10-15-signed-builds: `human` (a person submitted the
* run), `agent` (an agent session submitted it) and `system` (zion-build itself
* started it, such as a trigger). They are the values of zion-build
* `runs.submitter_kind`. The same set types the `submitterKind` member of a
* build provenance statement (`buildProvenanceExternalParametersSchema`), so the
* job token and the signed statement share one word list. Frozen.
*/
const SUBMITTER_KINDS = Object.freeze([
	"human",
	"agent",
	"system"
]);
/**
* The claims of a zion-build job ID token that the scoped exchange compares with
* the CP release sources, in this order: `repository` (`<owner>/<name>`, lower
* case), `ref` (`refs/heads/<branch>` or `refs/tags/<tag>`) and `workflow` (a
* `.zion/workflows/<name>.yml` path). Each must be an own string claim of the
* verified assertion; an assertion that lacks one, or holds a non-string, gets
* no scoped token. Frozen.
*/
const RELEASE_SOURCE_ASSERTION_CLAIMS = Object.freeze([
	"repository",
	"ref",
	"workflow"
]);
/**
* The zod form of a {@link ZION_APP_CLAIM} value: a UUID of any version in lower
* case, the form of `release.apps.id`. An upper-case spelling is refused, so one
* app has one claim value. No `.transform()`.
*/
const zionAppClaimSchema = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/).describe("The app uuid a release-source scoped token may publish");
/**
* The zod form of a {@link RUN_DEADLINE_CLAIM} value: a whole, positive number of
* epoch seconds no larger than `Number.MAX_SAFE_INTEGER`. A fraction, a string, a
* zero and a negative number are refused. It reads no clock: the exchange
* compares the value with the current second.
*/
const runDeadlineClaimSchema = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
/**
* The canonical unpadded base64url form of 32 bytes: 43 characters, the last of
* which carries 4 data bits and 2 zero bits, so only 16 characters can end it.
* A non-canonical spelling of the same bytes is refused, so one value has one
* spelling and a hash or a `UNIQUE` index over it cannot be dodged.
*/
const BASE64URL_32_BYTES = /^[A-Za-z0-9_-]{42}[AEIMQUYcgkosw048]$/;
/**
* The form of an enroll token: 32 random bytes in canonical unpadded base64url,
* 43 characters (DF-14). core-api mints it with `randomBytes(32).toString('base64url')`,
* shows it once, stores its SHA-256 hash, and accepts it once within one hour.
*/
const MACHINE_ENROLL_TOKEN_PATTERN = BASE64URL_32_BYTES;
/** The longest host name DNS allows, in characters (RFC 1035 §2.3.4 and RFC 1123 §2.1). */
const HOST_NAME_MAX_LENGTH = 253;
/**
* The exchange request body, RFC 8693 §2.1. core-api and cp-auth-api accept it
* as `application/x-www-form-urlencoded` and as JSON, and this one schema
* validates both.
*
* - `grant_type` must be {@link TOKEN_EXCHANGE_GRANT_TYPE}.
* - `subject_token` is the compact JWS assertion, 1 to
*   {@link MAX_SUBJECT_TOKEN_LENGTH} characters.
* - `subject_token_type` must be {@link SUBJECT_TOKEN_TYPE_JWT}.
* - `audience` is the plane's audience: on the data plane, the org issuer
*   `https://<org host>` (D-007).
*
* `grant_type` and `subject_token_type` are bounded strings of 1 to 256
* characters, and `audience` of 1 to 267 (R-028: `https://`, a 253-character
* org host and 6 characters of headroom). None is a literal. A wrong value
* passes this schema and the
* route refuses it with its own RFC 6749 code (`unsupported_grant_type`, or
* `invalid_request` for the other two) and one precise reason in the audit row
* (DF-3). A literal would turn each of those into the pipe's generic 400.
*
* `.strict()`: the five RFC 8693 parameters the exchange does not implement
* (`resource`, `scope`, `requested_token_type`, `actor_token`,
* `actor_token_type`) and any other key are refused with a 400. A silently
* ignored `scope` would look like a down-scoping mechanism that does not exist.
*/
const federatedTokenRequestSchema = z.object({
	grant_type: z.string().min(1).max(256).describe(`Must be \`${TOKEN_EXCHANGE_GRANT_TYPE}\``),
	subject_token: z.string().min(1).max(MAX_SUBJECT_TOKEN_LENGTH).describe("The OIDC assertion, a compact JWS, that the issuer minted or the machine signed"),
	subject_token_type: z.string().min(1).max(256).describe(`Must be \`${SUBJECT_TOKEN_TYPE_JWT}\``),
	audience: z.string().min(1).max(267).describe("The plane's audience; on the data plane, the org issuer https://<org host>")
}).strict();
/**
* The exchange success response, RFC 8693 §2.2.1. The planes produce it and the
* SDK clients parse it.
*
* - `access_token` is a compact JWS of at most 65,536 characters. A minted
*   data-plane token is under 2 kB; the bound is a chosen ceiling, 32 times
*   that, that stops a client from buffering an unbounded string, and has no
*   other source.
* - `issued_token_type` is {@link SUBJECT_TOKEN_TYPE_JWT}.
* - `token_type` is `Bearer`. An `external` token carries no `cnf` (D-017).
* - `expires_in` is a whole number of seconds from 1 to
*   {@link FEDERATED_TOKEN_MAX_TTL_SECONDS}.
*
* `.strict()`, and there is no `refresh_token`: a response that carries one, or
* any other extra key, fails to parse (D-005). A machine re-exchanges a fresh
* assertion instead of holding anything that outlives `expires_in`.
*/
const federatedTokenResponseSchema = z.object({
	access_token: z.string().max(65536).regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/, "access_token: a compact JWS").describe("The minted bearer token"),
	issued_token_type: z.literal(SUBJECT_TOKEN_TYPE_JWT),
	token_type: z.literal("Bearer"),
	expires_in: z.number().int().min(1).max(900).describe(`Seconds until the token expires; at most 900`)
}).strict();
/**
* The exchange success response of a release-source scoped exchange (feature
* 2026-10-15-signed-builds, D7). The members and every rule are those of
* {@link federatedTokenResponseSchema}, except that `expires_in` runs from 1 to
* {@link FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS}. It accepts every response that
* {@link federatedTokenResponseSchema} accepts.
*
* The shared exchange checks a scoped token against it, and an unscoped token
* against {@link federatedTokenResponseSchema}, so an unscoped token still never
* lives past 900 s. A client that exchanges a zion-build assertion parses the
* answer with it. The 900 s schema stays as it is, so every client that parses
* it today behaves as before. `.strict()`, no `refresh_token`.
*/
const federatedReleaseTokenResponseSchema = federatedTokenResponseSchema.extend({ expires_in: z.number().int().min(1).max(FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS).describe(`Seconds until the token expires; at most ${FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS}`) });
/** The base64url alphabet, in value order (RFC 4648 §5). */
const BASE64URL_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
/**
* Decode a string that matches {@link BASE64URL_32_BYTES} into its 32 bytes.
* Pure JavaScript, so the contract stays usable in a browser bundle.
*/
function base64Url32ToBytes(encoded) {
	const bytes = /* @__PURE__ */ new Uint8Array(32);
	let buffer = 0;
	let bufferedBits = 0;
	let next = 0;
	for (const character of encoded) {
		buffer = (buffer << 6 | BASE64URL_ALPHABET.indexOf(character)) & 16383;
		bufferedBits += 6;
		if (bufferedBits >= 8) {
			bufferedBits -= 8;
			bytes[next] = buffer >> bufferedBits & 255;
			next += 1;
		}
	}
	return bytes;
}
/**
* The canonical little-endian `y` of each small-order Ed25519 point, with the
* sign bit clear, as hex: `y = 0` (order 4), `y = 1` (the identity), the two
* order-8 `y` values, and `y = p - 1` (order 2). With the sign bit masked off,
* each matches both of its sign forms.
*/
const ED25519_SMALL_ORDER_Y = [
	"0000000000000000000000000000000000000000000000000000000000000000",
	"0100000000000000000000000000000000000000000000000000000000000000",
	"26e8958fc2b227b045c3f489f2ef98f0d5dfac05d3c63339b13802886d53fc05",
	"c7176a703d4dd84fba3c0b760d10670f2a2053fa2c39ccc64ec7fd7792ac037a",
	"ecffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff7f"
];
/**
* True when `x` is a canonical 32-byte base64url Ed25519 public key that is
* neither a small-order point nor a non-canonical `y` (see
* {@link principalPublicJwkSchema}). A string that is not canonical 32-byte
* base64url answers true, so the `BASE64URL_32_BYTES` regex alone reports it.
*/
function isAcceptableEd25519PublicKey(x) {
	if (!BASE64URL_32_BYTES.test(x)) return true;
	const y = base64Url32ToBytes(x);
	y[31] = y[31] & 127;
	if (y[31] === 127 && y.subarray(1, 31).every((byte) => byte === 255) && y[0] >= 237) return false;
	const hex = Array.from(y, (byte) => byte.toString(16).padStart(2, "0")).join("");
	return !ED25519_SMALL_ORDER_Y.includes(hex);
}
/**
* The public JWK an `external` machine registers, as an admin pastes it or the
* machine presents it at enroll. Two key types, discriminated on `kty`:
*
* - `OKP` with `crv: 'Ed25519'` and `x`; `alg`, when present, is `EdDSA`.
* - `EC` with `crv: 'P-256'`, `x` and `y`; `alg`, when present, is `ES256`.
*
* Each coordinate is 32 bytes in canonical unpadded base64url, 43 characters.
* Each arm is `.strict()`, so a JWK carrying the private member `d`, a
* caller-chosen `kid`, `use`, `key_ops` or any other member is refused (DF-3).
* The server computes the RFC 7638 `kid` itself, so a caller can neither choose
* nor squat one under the table's global `UNIQUE (kid)`.
*
* The OKP arm refuses two classes of Ed25519 `x` that `createPublicKey` imports
* without complaint (DF-3):
*
* - A small-order point: the identity, the point of order 2, the two points of
*   order 4 and the four points of order 8, each with either sign bit, plus the
*   non-canonical spellings `p` and `p + 1` of `y = 0` and `y = 1`. That is the
*   14-encoding list libsodium's `has_small_order` refuses. Against the identity
*   point, the one signature `R = identity, S = 0` verifies for every message
*   under Node's `crypto.verify` and under jose, so a registered identity key
*   lets anyone who knows the principal's `iss` mint an assertion for any `sub`,
*   `jti` and `exp`.
* - A non-canonical `y`, one of `p` to `2^255 - 1`: RFC 8032 §5.1.3 refuses it,
*   and a second spelling of one point would dodge the `UNIQUE (kid)` index.
*
* The EC arm needs no such list: P-256 has cofactor 1, and the writer imports
* the key with `createPublicKey`, which refuses a point off the curve, before it
* stores it. The schema is the only guard on an Ed25519 key. Callers: WS-5's
* enroll and admin key routes, WS-3's admin key schema, WS-8's control-plane key
* route.
*/
const principalPublicJwkSchema = z.discriminatedUnion("kty", [z.object({
	kty: z.literal("OKP"),
	crv: z.literal("Ed25519"),
	x: z.string().regex(BASE64URL_32_BYTES, "x: 32 bytes, canonical base64url").refine(isAcceptableEd25519PublicKey, "x: a small-order or non-canonical Ed25519 point"),
	alg: z.literal("EdDSA").optional()
}).strict(), z.object({
	kty: z.literal("EC"),
	crv: z.literal("P-256"),
	x: z.string().regex(BASE64URL_32_BYTES, "x: 32 bytes, canonical base64url"),
	y: z.string().regex(BASE64URL_32_BYTES, "y: 32 bytes, canonical base64url"),
	alg: z.literal("ES256").optional()
}).strict()]);
/**
* The enroll request body (D-012): the enroll token and the machine's public
* JWK. The token identifies the principal and the org; the body names neither.
*
* - `enrollToken` matches {@link MACHINE_ENROLL_TOKEN_PATTERN}.
* - `publicJwk` matches {@link principalPublicJwkSchema}.
*
* `.strict()`, so any other key is a 400. A well-formed token that is unknown,
* expired, spent or from another org passes this schema; the route refuses it
* with 401 and one precise reason in the audit row (DF-3).
*/
const machineEnrollRequestSchema = z.object({
	enrollToken: z.string().regex(MACHINE_ENROLL_TOKEN_PATTERN, "enrollToken: 32 bytes, canonical base64url").describe("The one-use enroll token an org admin minted for this principal"),
	publicJwk: principalPublicJwkSchema.describe("The public half of the key the machine signs its assertions with")
}).strict();
/**
* The built-in org roles an `external` principal may never hold (R-012, DF-3),
* in the order core-api's `BUILTIN_ROLES` lists them.
*
* - `owner`: transfers ownership and archives the org.
* - `admin`: holds every org permission but `org:manage`, `settings:identity:manage`
*   among them. R-015 names that permission as the gate on every
*   credential-granting write: register a key, mint an enroll token, create or
*   re-enable a trust policy. An `external` admin could mint credentials for
*   itself and for every other `external` principal.
* - `platform-agent-runtime` ({@link AGENT_HOST_ROLE_SLUG}): issues per-unit
*   agent capabilities across orgs.
* - `platform-machine-enroller` ({@link MACHINE_ENROLLER_ROLE_SLUG}): enrolls
*   and revokes machine principals and declares agent hosts.
*
* A system outside the platform holding any of them could grant itself or
* another machine new credentials. An org owner holds every permission, so an
* escalation check alone would let an owner assign all four; a caller refuses
* them by name. Callers: WS-3's admin create schema refuses the slugs before a
* request leaves the client; WS-5's create route and role writes refuse them
* on the server. Frozen, so no caller can widen the list at run time.
*
* The list names built-in roles only. A custom role is refused by what it holds:
* {@link EXTERNAL_PRINCIPAL_REFUSED_PERMISSIONS} (R-025).
*
* This package pins the four slugs as literals. The test that pins the list as a subset of
* core-api's `BUILTIN_ROLES` lives in core-api beside that constant and lands with WS-4 (R-036),
* because a contracts package cannot import a core-api symbol.
*/
const EXTERNAL_PRINCIPAL_REFUSED_ROLE_SLUGS = Object.freeze([
	"owner",
	"admin",
	AGENT_HOST_ROLE_SLUG,
	MACHINE_ENROLLER_ROLE_SLUG
]);
/**
* The org permissions a role held by an `external` principal may not grant (R-025, DF-3), in four
* groups and in this order:
*
* 1. The settings and org keys: `settings:identity:manage`, `settings:manage`, `org:manage`, `*`.
*    - `settings:identity:manage` is the gate on every credential-granting write (R-015): register
*      a key, mint an enroll token, create or re-enable a trust policy.
*    - `settings:manage` implies `settings:identity:manage` through `ORG_PERMISSION_IMPLICATIONS`.
*    - `org:manage` transfers ownership and archives the org.
*    - `*` grants every permission.
* 2. The platform keys of the two refused built-in machine roles, in core-api's
*    `MACHINE_PERMISSION_KEYS` order then `AGENT_CAPABILITY_PERMISSION_KEYS`:
*    `platform:machine-principal:enroll`, `platform:machine-principal:enroll-host`,
*    `platform:machine-principal:revoke`, `platform:delegation:exchange`, `platform:delegation:mint`,
*    `platform:agent-capability:issue`. `platform-machine-enroller` holds the first, second and
*    third; `platform-agent-runtime` holds the sixth. Without them a custom role copied from either
*    built-in would pass where its slug is refused.
* 3. The two other platform credential keys core-api registers for roles:
*    `platform:nats:identity-mint` (`NATS_PERMISSION_KEYS`) brokers a NATS credential, and
*    `platform:service-token:mint` (`SERVICE_TOKEN_PERMISSION_KEYS`) trades a bearer for a
*    3,600 s `principalKind: 'service'` token on the public `POST /v1/auth/service-token` route.
* 4. `resource-grant:manage`, the second entry of `RESOURCE_GRANT_RESERVED_ORG_PERMISSION_KEYS`:
*    it creates and revokes grants on every resource of the org, the principal's own included.
*
* {@link EXTERNAL_PRINCIPAL_REFUSED_ROLE_SLUGS} refuses the built-in roles by name; this list
* refuses a custom role by its permissions. WS-5 refuses, at create and at every role write to an
* `external` principal (direct and group-inherited), any role that holds one of these entries.
* The check is `hasPermission(rolePermissions, entry)` for each entry (R-035), which reads the
* wildcard, a prefix wildcard such as `settings:*`, and `ORG_PERMISSION_IMPLICATIONS`. So a role
* holding `settings:*`, `settings:identity:*`, `platform:*` or `platform:delegation:*` is refused,
* which an exact-string intersection would admit.
*
* Every key of the org catalog that grants `settings:identity:manage` under `hasPermission` is on
* the list, and so is every `platform:` key core-api's `ALL_REGISTERED_ORG_PERMISSION_KEYS` lets an
* owner write into a role. This package cannot import that core-api set, so the platform keys are
* literals here; the test that pins them against it belongs beside the set in core-api. The two
* app-consent keys `platform:org-members:read` and `platform:groups:manage` are absent: core-api
* registers neither for a role, so no role can hold them. Frozen, so no caller can widen or
* shrink the list at run time.
*/
const EXTERNAL_PRINCIPAL_REFUSED_PERMISSIONS = Object.freeze([
	OrgPermission.SettingsIdentityManage,
	OrgPermission.SettingsManage,
	OrgPermission.OrgManage,
	"*",
	"platform:machine-principal:enroll",
	"platform:machine-principal:enroll-host",
	"platform:machine-principal:revoke",
	"platform:delegation:exchange",
	"platform:delegation:mint",
	"platform:agent-capability:issue",
	"platform:nats:identity-mint",
	"platform:service-token:mint",
	RESOURCE_GRANT_RESERVED_ORG_PERMISSION_KEYS[1]
]);
/**
* The longest OIDC issuer, in characters. An issuer is a URL a policy stores and compares with
* `iss` byte for byte, and 2,048 is the URL length every mainstream HTTP stack and browser accepts.
* The known issuers this feature names are under 100 characters: GitHub Actions 41, Google 27, a
* GKE cluster about 90 (R-001).
*/
const OIDC_ISSUER_MAX_LENGTH = 2048;
/** The path segment between the org host and the principal id in a registered-key issuer (R-001). */
const REGISTERED_KEY_ISSUER_SEGMENT = "machine-principals";
/**
* The longest registered-key issuer: `https://` (8), a 253-character host, `:65535` (6),
* `/machine-principals/` (20) and a 36-character UUID.
*/
const EXTERNAL_ISSUER_MAX_LENGTH = 323;
/**
* A lower-case host of one or more DNS labels (RFC 1123 §2.1): each label 1 to 63 letters, digits
* or hyphens, with no leading or trailing hyphen, joined by single dots, with no trailing dot. The
* label class excludes the dot, so the pattern runs in linear time.
*/
const DNS_HOST = "[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*";
/** An optional port of 1 to 99999 with no leading zero; the URL check refuses one above 65535. */
const OPTIONAL_PORT = "(?::[1-9][0-9]{0,4})?";
/**
* `https://`, a {@link DNS_HOST}, an optional port, then `/machine-principals/` and a lower-case
* UUID of any version. Nothing else.
*/
const EXTERNAL_ISSUER = new RegExp(`^https://${DNS_HOST}${OPTIONAL_PORT}/${REGISTERED_KEY_ISSUER_SEGMENT}/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\$`);
/**
* `https://`, a {@link DNS_HOST}, an optional port, and an optional path of non-empty segments
* with no `?`, `#`, backslash or whitespace, which may end in one `/`. The JSON Schema form of
* {@link oidcIssuerSchema}; the URL check below does the rest. Each repeated segment ends in a `/`
* its class excludes, so the pattern runs in linear time.
*
* Exported for app-oidc: `fixture.schema.json` carries its `source` verbatim as the `pattern` of
* `$defs/oidcIssue/properties/audiences/items`, and a test pins the two equal. Pure data.
*/
const OIDC_ISSUER_PATTERN = new RegExp(`^https://${DNS_HOST}${OPTIONAL_PORT}(?:/(?:[^/?#\\\\\\s]+/)*[^/?#\\\\\\s]*)?$`);
/** A host the WHATWG URL parser has read as an IPv4 address (always dotted decimal) or an IPv6 one. */
const IP_LITERAL_HOST = /^(?:[0-9]+(?:\.[0-9]+){3}|\[.*\])$/;
/**
* True when the WHATWG URL parser reads `issuer` back as the same string, with only the trailing
* `/` it appends to a bare origin, and the URL names a DNS host of at most 253 characters with no
* user info.
*
* - The parser throws on a port above 65535 or an invalid host, which answers false.
* - It rewrites an upper-case host, the default port `:443`, a shorthand IPv4 host such as `127.1`
*   and a `.` or `..` path segment, so the string differs, which answers false.
* - An IP-literal host answers false: a discovered issuer is fetched over the network, and an org
*   host is always a DNS name (DF-3).
*/
function isCanonicalIssuerUrl(issuer) {
	let url;
	try {
		url = new URL(issuer);
	} catch {
		return false;
	}
	if (url.href !== issuer && url.href !== `${issuer}/`) return false;
	if (url.username !== "" || url.password !== "") return false;
	if (IP_LITERAL_HOST.test(url.hostname)) return false;
	return url.hostname.length <= HOST_NAME_MAX_LENGTH;
}
/**
* The form of a third-party OIDC issuer a trust policy names: GitHub Actions, Google, a GKE or EKS
* cluster, any IdP that publishes `/.well-known/openid-configuration` (R-001, R-020). The plane fetches
* the issuer's discovery document and JWKS, and compares the stored issuer with the assertion's
* `iss` byte for byte.
*
* Valid: an `https` URL of at most 2,048 characters whose host is a lower-case DNS name of at most
* 253 characters (labels of 1 to 63 letters, digits or hyphens, no leading or trailing hyphen),
* with an optional port and a path of non-empty segments, that `new URL(issuer).href` gives back
* unchanged (with only the `/` the parser appends to a bare origin). One trailing slash is valid,
* because some IdPs (Auth0) put one in `iss`; an upper-case path is valid, because EKS issuers
* carry one.
*
* Refused (DF-3, R-020): `http://`; user info; a query or a fragment, even an empty one; whitespace or a
* backslash; an empty, hyphen-edged or 64-character host label, a trailing dot, or an underscore
* in the host; an empty path segment (`//`); an upper-case host, a default `:443` port, a port of
* 0, with a leading zero or above 65535, a `.` or `..` path segment, or any other spelling the
* parser rewrites; an IPv4 or IPv6 literal host; a host over 253 characters; a string over 2,048
* characters. R-020 sets this grammar for both issuer schemas.
*
* Reachability is not syntax: a single-label host such as `localhost` and a reserved suffix such as
* `.internal` pass this schema. `refuseUndiscoverableIssuer` in `@zion/platform-identity`
* refuses them, at exchange and at policy creation (R-020).
*
* Every string {@link externalIssuerSchema} admits, this schema admits too. Callers: WS-3's
* trust-policy schemas for a discovered issuer, and the planes' policy writers (WS-5, WS-8).
*/
const oidcIssuerSchema = z.string().max(OIDC_ISSUER_MAX_LENGTH).regex(OIDC_ISSUER_PATTERN, "issuer: an https URL with a lower-case DNS host, no user info, query, fragment or empty path segment").refine(isCanonicalIssuerUrl, "issuer: an https URL with a DNS host that the URL parser reads back unchanged");
/**
* The form of a registered-key issuer (R-001): the `iss` an `external` machine puts in an
* assertion it signs with its own key, and the issuer a trust policy for that key names.
* `https://<org host>/machine-principals/<principal uuid>`, where `<org host>` is the host of the
* plane's issuer record. `registeredKeyIssuer()` in `@zion/platform-identity` mints it and
* `parseRegisteredKeyIssuer()` reads the principal id back out of it.
*
* Valid: `https://`, a lower-case host of one or more DNS labels (each 1 to 63 letters, digits or
* hyphens, with no leading or trailing hyphen, at most 253 characters in all), an optional port
* of 1 to 65535 other than 443, then `/machine-principals/` and a lower-case UUID. At most 323
* characters. `new URL(issuer).href` gives the same string back, so a client that parses the
* issuer before it signs puts the same bytes in `iss`.
*
* Refused (DF-3, R-020): `http://`; an upper-case host or UUID; an empty or hyphen-edged host label; a
* host over 253 characters; an IP-literal host; user info, a query or a fragment; any path other
* than `/machine-principals/<uuid>`, a trailing slash included; a port of 0, with a leading zero,
* above 65535, or the default `:443`; a host the parser rewrites, such as `127.1`.
*
* Callers: {@link machineEnrollResponseSchema} here; WS-3's `adminMachinePrincipalSchema` and its
* key-registration response, and a trust policy whose issuer is a registered key; WS-1's
* `registeredKeyIssuer()` as the check on what it mints. {@link issuerNamesPrincipal} says which
* principal a valid one names.
*/
const externalIssuerSchema = z.string().max(EXTERNAL_ISSUER_MAX_LENGTH).regex(EXTERNAL_ISSUER, "issuer: https://<lower-case org host>/machine-principals/<lower-case uuid>").refine(isCanonicalIssuerUrl, "issuer: an https URL with a DNS host that the URL parser reads back unchanged");
/**
* True when `issuer` is a registered-key issuer (R-001) of `principalId`: `issuer` has the
* {@link externalIssuerSchema} form and its path is `/machine-principals/<principalId>` (R-029).
*
* The issuer form carries a lower-case UUID, so a `principalId` in upper case, or with any
* character the path does not end with, answers false. Every other input answers false too: an
* issuer that fails {@link externalIssuerSchema}, a discovered OIDC issuer, a bare org origin, and
* an issuer that names another principal. The function has no side effects and never throws.
*
* Callers: {@link machineEnrollResponseSchema} here; WS-3's `registerMachinePrincipalKeyResponseSchema`;
* WS-5's trust-policy writer, which refuses a registered-key policy whose issuer names a principal
* other than the policy's (R-023). One predicate, so the three cannot diverge.
*/
function issuerNamesPrincipal(issuer, principalId) {
	if (!externalIssuerSchema.safeParse(issuer).success) return false;
	return issuer.endsWith(`/${REGISTERED_KEY_ISSUER_SEGMENT}/${principalId}`);
}
/**
* The enroll success response: what the machine needs to sign its assertions.
*
* - `principalId` is the `external` principal's UUID, which the machine's
*   assertions carry as `sub`.
* - `kid` is the RFC 7638 SHA-256 thumbprint the server computed over the
*   registered key, 43 base64url characters, which the machine puts in the JWS
*   header.
* - `issuer` is the registered-key issuer of this principal (R-001),
*   `https://<org host>/machine-principals/<principalId>`, which the machine's
*   assertions carry as `iss` and which a trust policy for the key names. It has
*   the {@link externalIssuerSchema} form.
*
* `.strict()`: a response with any other key fails to parse. A response whose
* `issuer` names a principal other than `principalId`, by
* {@link issuerNamesPrincipal}, fails to parse too (DF-3, R-029), so a client
* never signs as one principal with another's issuer.
*/
const machineEnrollResponseSchema = z.object({
	principalId: z.string().uuid().describe("The external principal; the assertion sub"),
	kid: z.string().regex(BASE64URL_32_BYTES, "kid: an RFC 7638 SHA-256 thumbprint").describe("The server-computed RFC 7638 key id; the assertion header kid"),
	issuer: externalIssuerSchema.describe("The issuer URI of the registered key; the assertion iss")
}).strict().refine((response) => issuerNamesPrincipal(response.issuer, response.principalId), {
	message: "issuer: names another principal than principalId",
	path: ["issuer"]
});
/**
* The grammar of an outside claim name that the platform carries or compares (app-oidc): a letter,
* then up to 63 letters, digits or underscores. The GitHub Actions names (`job_workflow_ref`,
* `repository`), the Google names (`email`, `email_verified`) and the Kubernetes scalar names fit.
* A name with a dot, a colon, a hyphen or a slash, and a name that starts with `_` (so
* `__proto__`), does not.
*/
const OIDC_CLAIM_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
/** The most outside claims one `oidc` member carries (overview security table). */
const OIDC_FORWARDED_CLAIMS_MAX = 32;
/** The longest string claim value the platform carries or compares, in characters. */
const OIDC_CLAIM_VALUE_MAX_LENGTH = 256;
/**
* The claim names {@link forwardOidcClaims} never carries, in this order:
*
* 1. The registered JWT claims `iss`, `sub`, `aud`, `exp`, `nbf`, `iat`, `jti` (RFC 7519 §4.1).
*    `iss` and `sub` already sit in their own members, and the other five describe the assertion,
*    not the caller.
* 2. The OIDC Core claims about the login, not the caller: `nonce`, `auth_time`, `at_hash`,
*    `c_hash`, `azp`, and the RFC 7800 `cnf`.
* 3. `constructor` and `prototype`, two names that read as members of every JavaScript object.
*    {@link OIDC_CLAIM_NAME_PATTERN} already refuses `__proto__`.
*
* Frozen, so no caller can widen or shrink the list at run time.
*/
const OIDC_FORWARD_RESERVED_CLAIMS = Object.freeze([
	"iss",
	"sub",
	"aud",
	"exp",
	"nbf",
	"iat",
	"jti",
	"nonce",
	"auth_time",
	"at_hash",
	"c_hash",
	"azp",
	"cnf",
	"constructor",
	"prototype"
]);
const OIDC_FORWARD_RESERVED = new Set(OIDC_FORWARD_RESERVED_CLAIMS);
/**
* True when `name` is a claim name the platform can carry or compare: it matches
* {@link OIDC_CLAIM_NAME_PATTERN} and is not in {@link OIDC_FORWARD_RESERVED_CLAIMS}. Never throws.
* Pure. The exchange (through {@link forwardOidcClaims}), the fixture rule on `accept[].claims` and
* `@AcceptOidc` share it, so a route can require exactly the names a token can carry.
*/
function isForwardableOidcClaimName(name) {
	return typeof name === "string" && OIDC_CLAIM_NAME_PATTERN.test(name) && !OIDC_FORWARD_RESERVED.has(name);
}
/**
* The string form the exchange carries for one outside claim value, or `undefined` when the value
* is not carried:
*
* - a string of at most {@link OIDC_CLAIM_VALUE_MAX_LENGTH} characters: the same string;
* - a finite number: `String(value)`, so `3` gives `'3'`, `1.5` gives `'1.5'` and `-0` gives `'0'`;
* - a boolean: `'true'` or `'false'`;
* - a longer string, a non-finite number, `null`, an object or an array: `undefined`.
*/
function forwardedOidcClaimValue(value) {
	if (typeof value === "string") return value.length <= 256 ? value : void 0;
	if (typeof value === "number") return Number.isFinite(value) ? String(value) : void 0;
	if (typeof value === "boolean") return value ? "true" : "false";
}
/**
* The outside claims a federated token carries in `oidc.claims` (app-oidc, D11, overview security
* table). core-api (WS-2) calls it once per exchange with the verified assertion payload.
*
* Input: the verified payload as an object. A value that is not a plain object (`null`, an array,
* a string) gives the empty map.
*
* Output: a new object with no prototype. It holds each own enumerable key of `payload` that:
*
* 1. passes {@link isForwardableOidcClaimName}, so no reserved name and no name outside the
*    grammar; and
* 2. holds a string of at most {@link OIDC_CLAIM_VALUE_MAX_LENGTH} characters, a finite number or
*    a boolean. A longer string is dropped whole and never cut, so an equality test on the value
*    cannot pass on a prefix.
*
* Each value in the output is a string. A finite number becomes `String(value)` (`3` gives `'3'`)
* and a boolean becomes `'true'` or `'false'`. A route requires string values only
* (`@AcceptOidc({ claims })`, `capabilities.oidc.accept[].claims`) and compares with `===`, so the
* string form is what lets a route require a number or boolean claim such as Google's
* `email_verified` (findings WS-1-R1, WS-1-R4).
*
* The keys are taken in code-unit order, and the first {@link OIDC_FORWARDED_CLAIMS_MAX} kept keys
* survive. So the result does not depend on the issuer's key order. Every other key is dropped
* with no error: the assertion is already verified, and the drop only narrows what an app sees.
*
* Invariants: the input stays as it is; the same input gives the same output; the output never
* holds more than {@link OIDC_FORWARDED_CLAIMS_MAX} keys. No I/O. Never throws.
*/
function forwardOidcClaims(payload) {
	const out = Object.create(null);
	if (payload === null || typeof payload !== "object" || Array.isArray(payload)) return out;
	const source = payload;
	let kept = 0;
	for (const name of Object.keys(source).sort()) {
		if (kept === 32) break;
		if (!isForwardableOidcClaimName(name)) continue;
		const value = forwardedOidcClaimValue(source[name]);
		if (value === void 0) continue;
		out[name] = value;
		kept += 1;
	}
	return out;
}
/**
* The zod form of {@link OidcCallerIdentity}, for a reader that parses a verified token payload.
*
* - `iss`: {@link oidcIssuerSchema}. A registered-key issuer passes it too.
* - `sub`: 1 to 512 characters.
* - `claims`: at most {@link OIDC_FORWARDED_CLAIMS_MAX} entries; each key passes
*   {@link isForwardableOidcClaimName}; each value is a string of at most
*   {@link OIDC_CLAIM_VALUE_MAX_LENGTH} characters, a finite number or a boolean.
*
* `.strict()`: any other member is refused. No `.transform()`, so `createOpenApiDocument()` can
* render it. A payload that {@link forwardOidcClaims} built always parses.
*/
const oidcCallerIdentitySchema = z.object({
	iss: oidcIssuerSchema,
	sub: z.string().min(1).max(512),
	claims: z.record(z.string().regex(OIDC_CLAIM_NAME_PATTERN).refine((name) => isForwardableOidcClaimName(name), "claim name: reserved"), z.union([
		z.string().max(256),
		z.number(),
		z.boolean()
	])).refine((claims) => Object.keys(claims).length <= 32, { message: `claims: at most 32 entries` })
}).strict();
//#endregion
export { EXTERNAL_PRINCIPAL_REFUSED_PERMISSIONS, EXTERNAL_PRINCIPAL_REFUSED_ROLE_SLUGS, FEDERATED_ASSERTION_MAX_LIFETIME_SECONDS, FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS, FEDERATED_TOKEN_MAX_TTL_SECONDS, FEDERATED_TOKEN_PATH, MACHINE_ENROLL_PATH, MACHINE_ENROLL_TOKEN_PATTERN, MAX_SUBJECT_TOKEN_LENGTH, OIDC_ASSERTION_MAX_LIFETIME_SECONDS, OIDC_CLAIM_NAME_PATTERN, OIDC_CLAIM_VALUE_MAX_LENGTH, OIDC_FORWARDED_CLAIMS_MAX, OIDC_FORWARD_RESERVED_CLAIMS, OIDC_ISSUER_PATTERN, RELEASE_SOURCE_ASSERTION_CLAIMS, RUN_DEADLINE_CLAIM, SUBJECT_TOKEN_TYPE_JWT, SUBMITTER_KINDS, SUBMITTER_KIND_CLAIM, TOKEN_EXCHANGE_GRANT_TYPE, ZION_APP_CLAIM, externalIssuerSchema, federatedReleaseTokenResponseSchema, federatedTokenRequestSchema, federatedTokenResponseSchema, forwardOidcClaims, isForwardableOidcClaimName, issuerNamesPrincipal, machineEnrollRequestSchema, machineEnrollResponseSchema, oidcCallerIdentitySchema, oidcIssuerSchema, principalPublicJwkSchema, runDeadlineClaimSchema, zionAppClaimSchema };

//# sourceMappingURL=federated-token.js.map